import { useEffect, useState } from "react";
import { AlertCircle, Download, Loader2, FileImage } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SpreadsheetViewer } from "@/components/common/spreadsheet-viewer";

// ── File-type helpers ─────────────────────────────────────────────────────────

/**
 * Extract the lowercased extension from a URL or filename. For URLs, isolates
 * the pathname first so host dots (e.g. `example.com`) don't leak into the
 * detected extension. Strips query string and hash so signed URLs
 * (e.g. `.xlsx?X-Amz-…`) don't leak signature text into the detection.
 *
 * Returns "" when the input has no extension.
 */
export const getFileType = (source: string): string => {
  if (!source) return "";
  // Prefer parsing as a URL so host segments (example.com) don't pollute the
  // extension we extract. Fall back to raw string for bare filenames.
  let path = source;
  try {
    path = new URL(source).pathname;
  } catch {
    // Not a parseable URL — treat as a filename or relative path.
    path = source.split("?")[0].split("#")[0];
  }
  const filename = path.split("/").pop() ?? "";
  const dot = filename.lastIndexOf(".");
  return dot === -1 ? "" : filename.slice(dot + 1).toLowerCase();
};

export const isPDF = (type: string) => type.toLowerCase() === "pdf";
export const isPowerPoint = (type: string) => {
  const t = type.toLowerCase();
  return t === "ppt" || t === "pptx";
};
export const isWordDocument = (type: string) => {
  const t = type.toLowerCase();
  return t === "doc" || t === "docx";
};
export const isExcelFile = (type: string) => {
  const t = type.toLowerCase();
  return t === "xlsx" || t === "xls";
};
export const isCSVFile = (type: string) => type.toLowerCase() === "csv";
export const isImage = (type: string) => {
  const t = type.toLowerCase();
  return (
    t === "jpg" ||
    t === "jpeg" ||
    t === "png" ||
    t === "ico" ||
    t === "gif" ||
    t === "webp" ||
    t === "bmp"
  );
};

/**
 * Extension implied by a MIME type, for sources that carry one but whose file
 * name doesn't (email attachments regularly arrive as `scan` or `image001`).
 * Returns "" when the type isn't one the dialog has a branch for.
 */
export const extensionFromContentType = (contentType?: string): string => {
  const type = (contentType ?? "").split(";")[0].trim().toLowerCase();
  const map: Record<string, string> = {
    "application/pdf": "pdf",
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/gif": "gif",
    "image/webp": "webp",
    "image/bmp": "bmp",
    "text/csv": "csv",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "docx",
    "application/vnd.ms-powerpoint": "ppt",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      "pptx",
  };
  return map[type] ?? "";
};

/**
 * MIME type for an image extension. A blob has to be typed concretely — a
 * wildcard like `image/*` is not a type the browser will render.
 */
const imageMimeType = (extension: string): string => {
  const map: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    bmp: "image/bmp",
    ico: "image/x-icon",
  };
  return map[extension.toLowerCase()] ?? "image/png";
};

/**
 * The BE proxy that serves a stored file inline, with a content type derived
 * from its name. Prefer it over the raw storage URL for anything rendered or
 * embedded: storage objects can carry `Content-Disposition: attachment` (every
 * email attachment does) or no usable content type, and neither a browser nor
 * an embedded viewer will render those.
 */
export const fileProxyUrl = (fileId: string): string =>
  `${import.meta.env.VITE_API_URL}/view_file/${fileId}`;

/**
 * The Office and Google viewers fetch the document from their own servers, so
 * whatever we hand them has to be reachable from the public internet. Our
 * proxy is, in every deployed environment — but not when the API is a local
 * dev server, where the storage URL is the only thing they can see.
 */
const isPubliclyReachable = (url: string): boolean =>
  !/^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:|\/|$)/i.test(url);

/**
 * The url to hand an external viewer: our proxy when it can be reached from
 * the internet, the storage url otherwise.
 */
export const embedUrlFor = (url: string, fileId?: string): string => {
  const proxied = fileId ? fileProxyUrl(fileId) : null;
  return proxied && isPubliclyReachable(proxied) ? proxied : url;
};

/**
 * Only accept http(s) URLs for embedding. Rejects `javascript:`, `data:`,
 * `blob:`, `filesystem:`, `about:`, etc. — any of which could be used to
 * break out of the iframe or execute arbitrary code in the app's context.
 */
export const isSafeHttpUrl = (url: string): boolean => {
  try {
    const { protocol } = new URL(url);
    return protocol === "https:" || protocol === "http:";
  } catch {
    return false;
  }
};

// ── Internal: iframe with loader ─────────────────────────────────────────────

/**
 * Shared loader-overlay + iframe renderer used by PDF/Word/PowerPoint branches.
 * `referrerPolicy="no-referrer"` prevents leaking the signed URL via the
 * Referer header when the external viewer loads sub-resources.
 */
function IframeViewer({ src, title }: { src: string; title: string }) {
  return (
    <>
      <div className="absolute inset-0 flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
      <iframe
        src={src}
        className="w-full h-full"
        title={title}
        referrerPolicy="no-referrer"
        onLoad={(e) => {
          const iframe = e.target as HTMLIFrameElement;
          const loader = iframe.previousSibling as HTMLDivElement;
          if (loader) loader.style.display = "none";
        }}
      />
    </>
  );
}

// ── Internal: in-app (blob) viewer ───────────────────────────────────────────

/**
 * Fetches the bytes ourselves and renders them from a blob URL, so nothing
 * about the preview depends on a third party being able to reach our storage.
 *
 * The blob is typed here rather than from the response: files uploaded without
 * an explicit ContentType come back as `binary/octet-stream`, and the browser
 * will not render a PDF it has been told is a binary download.
 *
 * A blob URL is same-origin to the app, so framing it is unaffected by the
 * `X-Frame-Options: SAMEORIGIN` the proxy sends.
 */
export function BlobFileViewer({
  src,
  fileName,
  mimeType,
  kind,
}: {
  src: string;
  fileName: string;
  mimeType: string;
  kind: "document" | "image";
}) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;

    setObjectUrl(null);
    setError(null);

    // Plain fetch — NOT axiosInstance — so the Clerk Authorization header is
    // never attached to a storage URL. The proxy needs no auth.
    fetch(src)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
        const buf = await res.arrayBuffer();
        if (cancelled) return;
        created = URL.createObjectURL(new Blob([buf], { type: mimeType }));
        setObjectUrl(created);
      })
      .catch((err) => {
        if (cancelled) return;
        // Don't log `src` — it may be a signed storage link with credentials
        // in its query string.
        console.error("BlobFileViewer load error", { fileName }, err);
        setError("This file couldn't be loaded.");
      });

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [src, mimeType, fileName, attempt]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full gap-3 p-6 text-center">
        <AlertCircle className="w-10 h-10 text-muted-foreground/60" />
        <p className="text-sm font-medium text-foreground">{fileName}</p>
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAttempt((n) => n + 1)}
        >
          Try again
        </Button>
      </div>
    );
  }

  if (!objectUrl) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (kind === "image") {
    return (
      <div className="relative flex items-center justify-center w-full h-full p-4 overflow-hidden">
        <img
          src={objectUrl}
          alt={fileName}
          className="w-full h-full object-contain shadow-sm rounded-md"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full">
      <iframe src={objectUrl} className="w-full flex-1" title={fileName} />
      {/* The bytes are already in memory, so saving them costs nothing and
          keeps the user in the app — an anchor on the storage url ignores
          `download` cross-origin and lands them in a new tab instead. */}
      <div className="flex justify-end pt-2">
        <Button asChild variant="ghost" size="sm">
          <a href={objectUrl} download={fileName}>
            <Download className="h-4 w-4 mr-1.5" />
            Download
          </a>
        </Button>
      </div>
    </div>
  );
}

// ── Viewer component ─────────────────────────────────────────────────────────

interface FilePreviewDialogProps {
  url: string;
  fileName: string;
  /**
   * FileUpload `_id`. When present the dialog reads the bytes through the BE
   * proxy instead of the storage url, which is what makes PDFs and images
   * render at all for files stored with an attachment disposition.
   */
  fileId?: string;
  /** Trigger slot — e.g. an icon button. If omitted, caller controls `open`. */
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Override the extension detection (for cases where URL has no extension). */
  typeOverride?: string;
  /**
   * MIME type reported by the source, used only when neither the file name nor
   * the URL carries a usable extension. Email attachments often don't.
   */
  contentType?: string;
}

/**
 * Multi-format file previewer matching the Data Room / Portal preview UX.
 * Accepts a trigger via `children` (shadcn Dialog pattern) or external
 * controlled open state.
 */
export function FilePreviewDialog({
  url,
  fileName,
  fileId,
  children,
  open,
  onOpenChange,
  typeOverride,
  contentType,
}: FilePreviewDialogProps) {
  const fileType = typeOverride ?? getFileType(fileName || url);
  const resolvedType = fileType || extensionFromContentType(contentType);

  // Everything we can serve ourselves goes through the proxy. It is the only
  // URL guaranteed to be inline, correctly typed and publicly fetchable —
  // the stored object is none of those for files that arrived by email.
  const proxied = fileId ? fileProxyUrl(fileId) : null;
  // What an external viewer is given. Falls back to the storage URL when the
  // proxy is a local dev server those services can't reach.
  const embedUrl = embedUrlFor(url, fileId);

  const content = (
    <DialogContent className="max-w-4xl h-[80vh] overflow-hidden">
      <div className="relative w-full h-full overflow-hidden">
        {isPDF(resolvedType) ? (
          // Rendered in-app off a blob. The browser's own PDF viewer, no
          // round trip through Google, and no requirement that our storage be
          // reachable by anyone but us.
          proxied ? (
            <BlobFileViewer
              src={proxied}
              fileName={fileName}
              mimeType="application/pdf"
              kind="document"
            />
          ) : (
            <IframeViewer
              src={`https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`}
              title={fileName}
            />
          )
        ) : isPowerPoint(resolvedType) ? (
          <IframeViewer
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(embedUrl)}`}
            title={fileName}
          />
        ) : isWordDocument(resolvedType) ? (
          <IframeViewer
            src={`https://docs.google.com/viewer?url=${encodeURIComponent(embedUrl)}&embedded=true`}
            title={fileName}
          />
        ) : isCSVFile(resolvedType) || isExcelFile(resolvedType) ? (
          <SpreadsheetViewer url={url} fileName={fileName} fileId={fileId} />
        ) : isImage(resolvedType) ? (
          proxied ? (
            <BlobFileViewer
              src={proxied}
              fileName={fileName}
              mimeType={imageMimeType(resolvedType)}
              kind="image"
            />
          ) : (
            <div className="relative flex items-center justify-center w-full h-full p-4 overflow-hidden">
              <img
                src={url}
                alt={fileName}
                className="w-full h-full object-contain shadow-sm rounded-md"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = "none";
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = "flex";
                }}
              />
              <div
                className="flex-col items-center justify-center gap-2 text-gray-400"
                style={{ display: "none" }}
              >
                <FileImage className="w-16 h-16" />
                <p className="text-sm">Image preview not available</p>
              </div>
            </div>
          )
        ) : isSafeHttpUrl(url) ? (
          // Unknown extension: render inside a sandboxed iframe with a strict
          // referrer policy. `allow-same-origin` is intentionally omitted so
          // the embedded content cannot access cookies / localStorage.
          <iframe
            src={url}
            className="w-full h-full"
            title={fileName}
            sandbox="allow-scripts allow-popups allow-forms"
            referrerPolicy="no-referrer"
          />
        ) : (
          // Non-http(s) URL — never embed and never render a clickable link
          // for it, since the scheme (e.g. `javascript:`, `blob:`, `data:`)
          // could execute in the user's context or leak the token.
          <div className="flex flex-col items-center justify-center w-full h-full gap-3 p-6 text-center">
            <FileImage className="w-12 h-12 text-muted-foreground/60" />
            <p className="text-sm font-medium text-foreground">{fileName}</p>
            <p className="text-sm text-muted-foreground">
              This file can't be previewed or opened from here.
            </p>
          </div>
        )}
      </div>
    </DialogContent>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      {content}
    </Dialog>
  );
}
